import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

interface UserGroup {
  id: string;
  groupId: string;
  group: {
    id: string;
    name: string;
  };
}

interface EnvironmentRequest {
  id: string;
  requestNumber: string;
  environmentId: string;
  shortDescription: string;
  description?: string;
  status: string;
  approvalStatus: string;
  priority?: string;
  dueDate?: string;
  assignedToId?: string;
  assignmentGroupId?: string;
  approverGroupId?: string;
  expectedStart?: string;
  actualStart?: string;
  completedAt?: string;
  variables?: any;
  createdAt: string;
  updatedAt: string;
  environment?: {
    id: string;
    name: string;
    cloudProviderType: string;
    environmentType: string;
    status: string;
    company?: {
      id: string;
      name: string;
    };
  };
  createdBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  assignedTo?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  assignmentGroup?: {
    id: string;
    name: string;
  };
  approverGroup?: {
    id: string;
    name: string;
  };
  activities?: Array<{
    id: string;
    activityType: string;
    description: string;
    metadata?: any;
    performedAt: string;
    performedBy?: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
    };
  }>;
}

const EnvironmentRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { canUpdateEntity } = useUserPermissions();
  const { formatDateTime } = useDateFormatter();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    shortDescription: '',
    description: '',
    priority: 'MEDIUM'
  });

  const { data: request, isLoading, isError } = useQuery({
    queryKey: ['environment-request', id],
    queryFn: async () => {
      const response = await api.get(`/environment-requests/${id}`);
      return response.data as EnvironmentRequest;
    },
  });

  // Fetch user's groups to check if they're in Onboarding-Approver
  const { data: userGroups } = useQuery({
    queryKey: ['user-groups', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const response = await api.get(`/users/${user.id}`);
      return response.data.groups as UserGroup[];
    },
    enabled: !!user?.id,
  });

  const approveMutation = useMutation({
    mutationFn: async (comments?: string) => {
      const response = await api.post(`/environment-requests/${id}/approve`, { comments });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Request approved successfully');
      queryClient.invalidateQueries({ queryKey: ['environment-request', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to approve request');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (comments: string) => {
      const response = await api.post(`/environment-requests/${id}/reject`, { comments });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Request rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['environment-request', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to reject request');
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/environment-requests/${id}/complete`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Request completed successfully');
      queryClient.invalidateQueries({ queryKey: ['environment-request', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to complete request');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.patch(`/environment-requests/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Request updated successfully');
      queryClient.invalidateQueries({ queryKey: ['environment-request', id] });
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update request');
    },
  });

  // Set edit data when request is loaded
  React.useEffect(() => {
    if (request) {
      setEditData({
        shortDescription: request.shortDescription || '',
        description: request.description || '',
        priority: request.priority || 'MEDIUM'
      });
    }
  }, [request]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading request details...</div>
      </div>
    );
  }

  if (isError || !request) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading request details</div>
      </div>
    );
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Open':
        return 'bg-blue-100 text-blue-800';
      case 'InProgress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalBadgeColor = (status: string) => {
    switch (status) {
      case 'Requested':
        return 'bg-yellow-100 text-yellow-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if user is in Onboarding-Approver group (ONLY group members can approve)
  const isInApproverGroup = userGroups?.some(
    (ug) => ug.group.name === 'Onboarding-Approver'
  );
  const isApprover = isInApproverGroup;

  // Always show approval buttons for authenticated users when request status is 'Requested'  
  const showApprovalButtons = user && request && request.approvalStatus === 'Requested';
  const canApprove = isApprover;

  // Users can complete if they have update permission OR they are assigned to the request
  const canComplete = user && request.status === 'InProgress' &&
    (canUpdateEntity('environment-requests') || request.assignedToId === user.id);

  // Check if user can edit: has update permission OR is the creator
  const canEdit = user && request && (
    canUpdateEntity('environment-requests') ||
    request.createdBy?.id === user.id
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/environments"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Environments
        </Link>
        
        <div className="mt-4 md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              {request.requestNumber}
            </h2>
            {isEditing ? (
              <div className="mt-2 space-y-2">
                <input
                  type="text"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={editData.shortDescription}
                  onChange={(e) => setEditData({ ...editData, shortDescription: e.target.value })}
                  placeholder="Short Description"
                />
                <textarea
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  placeholder="Description"
                  rows={2}
                />
                <select
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={editData.priority}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                >
                  <option value="LOW">Low Priority</option>
                  <option value="MEDIUM">Medium Priority</option>
                  <option value="HIGH">High Priority</option>
                  <option value="CRITICAL">Critical Priority</option>
                </select>
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                {request.shortDescription}
              </p>
            )}
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
            {canEdit && (
              <>
                {isEditing ? (
                  <>
                    <button
                      onClick={() => updateMutation.mutate(editData)}
                      disabled={updateMutation.isPending}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditData({
                          shortDescription: request.shortDescription || '',
                          description: request.description || '',
                          priority: request.priority || 'MEDIUM'
                        });
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <PencilIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                    Edit
                  </button>
                )}
              </>
            )}
            {showApprovalButtons && !isEditing && (
              <>
                <div 
                  title={!canApprove ? "Don't have sufficient rights to approve or reject." : ""}
                  className="inline-block"
                >
                  <button
                    onClick={() => {
                      if (canApprove && window.confirm('Are you sure you want to approve this request?')) {
                        approveMutation.mutate(undefined);
                      }
                    }}
                    disabled={!canApprove || approveMutation.isPending}
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                      canApprove 
                        ? 'bg-green-600 hover:bg-green-700 cursor-pointer' 
                        : 'bg-gray-400 cursor-not-allowed'
                    } disabled:opacity-50`}
                  >
                    <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" />
                    Approve
                  </button>
                </div>
                <div 
                  title={!canApprove ? "Don't have sufficient rights to approve or reject." : ""}
                  className="inline-block"
                >
                  <button
                    onClick={() => {
                      if (canApprove) {
                        const comments = window.prompt('Please provide a reason for rejection:');
                        if (comments) {
                          rejectMutation.mutate(comments);
                        }
                      }
                    }}
                    disabled={!canApprove || rejectMutation.isPending}
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                      canApprove 
                        ? 'bg-red-600 hover:bg-red-700 cursor-pointer' 
                        : 'bg-gray-400 cursor-not-allowed'
                    } disabled:opacity-50`}
                  >
                    <XCircleIcon className="-ml-1 mr-2 h-5 w-5" />
                    Reject
                  </button>
                </div>
              </>
            )}
            {canComplete && !isEditing && (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to mark this request as complete?')) {
                    completeMutation.mutate();
                  }
                }}
                disabled={completeMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" />
                Complete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Request Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Request Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Request Information
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(request.status)}`}>
                      {request.status}
                    </span>
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Approval Status</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getApprovalBadgeColor(request.approvalStatus)}`}>
                      {request.approvalStatus}
                    </span>
                  </dd>
                </div>
                {request.priority && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Priority</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(request.priority)}`}>
                        {request.priority}
                      </span>
                    </dd>
                  </div>
                )}
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDateTime(request.createdAt)}
                  </dd>
                </div>
                {request.dueDate && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatDateTime(request.dueDate)}
                    </dd>
                  </div>
                )}
                {request.description && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.description}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Environment Details */}
          {request.environment && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Environment Details
                </h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                <dl className="sm:divide-y sm:divide-gray-200">
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.environment.name}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Cloud Provider</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.environment.cloudProviderType}
                    </dd>
                  </div>
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Type</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {request.environment.environmentType}
                    </dd>
                  </div>
                  {request.environment.company && (
                    <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Company</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {request.environment.company.name}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Assignment and Activities */}
        <div className="space-y-6">
          {/* Assignment Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Assignment
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5">
              <dl className="space-y-3">
                {request.assignedTo && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {request.assignedTo.firstName} {request.assignedTo.lastName}
                    </dd>
                  </div>
                )}
                {request.assignmentGroup && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Assignment Group</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {request.assignmentGroup.name}
                    </dd>
                  </div>
                )}
                {request.approverGroup && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Approver Group</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {request.approverGroup.name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Activity Timeline */}
          {request.activities && request.activities.length > 0 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Activity Timeline
                </h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5">
                <div className="flow-root">
                  <ul className="-mb-8">
                    {request.activities.map((activity, index) => (
                      <li key={activity.id}>
                        <div className="relative pb-8">
                          {index !== request.activities!.length - 1 && (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white">
                                <ClockIcon className="h-4 w-4 text-white" />
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div>
                                <p className="text-sm text-gray-900">
                                  {activity.description}
                                </p>
                                {activity.performedBy && (
                                  <p className="text-xs text-gray-500">
                                    by {activity.performedBy.firstName} {activity.performedBy.lastName}
                                  </p>
                                )}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {formatDateTime(activity.performedAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvironmentRequestDetail;